import { SeatCategory } from '@seatix/shared/domain';
import { ScannedBy, ScannedInfo } from './inventory-ticket.types';
import { SeatMinimal } from './ticket-core.types';

export type SeatCategoryMinimal = Pick<SeatCategory, 'id' | 'name'> & {
  color: string;
};

export enum TicketStatus {
  Active = 'active',
  Scanned = 'scanned',
  Cancelled = 'cancelled',
}

export interface TicketCore {
  ticket_id: number;
  holder_name: string;
  seat_label: string;
  status: TicketStatus;
  signature: string;
  seat_category_id: number;
}

export type TicketWithCategory = TicketCore & {
  seat_category_id: number;
};

export type TicketWithScanMetadata = TicketWithCategory & ScannedInfo;

export type InventoryTicket = TicketWithScanMetadata & {
  seat: SeatMinimal;
  seat_category?: SeatCategoryMinimal | null;
  cancelled_at?: number;
};

export type TicketWithCompleteScan = TicketWithCategory & ScannedInfo;

export type ScannedTicket = TicketWithCompleteScan & {
  seat: SeatMinimal;
};

export type StoredTicket = InventoryTicket & {
  event_id: number;
  cached_at: number;
};

export type OfflineTicket = Pick<TicketCore, 'ticket_id' | 'signature'>;

export interface ScanInfo {
  scan_id: string;
  scanned_at: number;
  scanned_by: ScannedInfo;
  device_id?: string;
}

export interface TicketDisplayData {
  holderName: string;
  seat: SeatMinimal;
  source: 'api' | 'cache';
}

export interface ScanTicketPayload {
  event_id: number;
  device_id: string;
  signature: string;
  prefix?: string;
  ticket_id: number;
  gate_id?: number | null;
  gate_name?: string | null;
  raw_qr?: string;
}

export interface ValidationResponse {
  code: number;
  message: string;
  data: {
    status: 'success' | 'error';
    scan_id: string;
    ticket: ScannedTicket;
    timestamp: number;
  };
}

export interface InventoryExportResponse {
  success: boolean;
  data: {
    status: 'new_data' | 'no_changes';
    generated_at: string;
    sync_to: number;
    tickets: Record<string, InventoryTicket>;
  };
}

export type { ScannedInfo, SeatMinimal };

export interface TicketValidationRequest {
  ticket_code: string;
  event_id: number;
  gate_id: number;
  scanner_id: string;
  timestamp: number;
  offline_mode?: boolean;
}

export interface TicketInfo {
  ticket_id: number;
  holder_name: string;
  seat_label: string;
  status: TicketStatus;
  seat: SeatMinimal;
  timestamp?: number;
}

export interface GateValidationResult {
  allowed: boolean;
  restriction_type: 'soft' | 'hard';
  message?: string;
  recommended_gates?: string[];
  seat_category?: {
    id: number;
    name: string;
    color?: string;
  };
}

export enum RejectionReason {
  NOT_FOUND = 'not_found',
  ALREADY_USED = 'already_used',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
  EXPIRED = 'expired',
  WRONG_EVENT = 'wrong_event',
  INVALID_FORMAT = 'invalid_format',
  GATE_RESTRICTION_SOFT = 'gate_restriction_soft',
  GATE_RESTRICTION_HARD = 'gate_restriction_hard',
  SUSPENDED = 'suspended',
  NOT_YET_VALID = 'not_yet_valid',
  NETWORK_ERROR = 'network_error',
}

export interface TicketValidationResponse {
  status: 'success' | 'valid' | 'invalid' | 'duplicate';
  reason?: RejectionReason;
  scan_id?: string;
  ticket: ScannedTicket;
  message: string;
}

export type ValidationStatus =
  | 'valid'
  | 'invalid'
  | 'already_scanned'
  | 'expired'
  | 'wrong_event'
  | 'cancelled';

export interface ValidationResult {
  status: ValidationStatus;
  message: string;
  ticket_data?: unknown;
  timestamp: number;
  offline_validation?: boolean;
}

export type { ScannedBy };

export interface ScanTicketPayloadBatchItem
  extends Omit<ScanTicketPayload, 'event_id'> {
  scanned_at: number;
}

export interface BatchScanRequest {
  scans: ScanTicketPayloadBatchItem[];
}

export interface BatchScanFailedItem {
  ticket_id: number;
  reason: 'cancelled' | 'not_found';
}

export interface BatchScanResponse {
  synced: number;
  already_synced: number;
  failed: BatchScanFailedItem[];
}
